import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import ApiService from "@/services/api.service";
import { useAuthStore } from "@/store/auth";

interface ExpertMiniProfileResponse {
  success: boolean;
  data: {
    profile_picture: string;
    name: string;
  };
}

/**
 * Composable to fetch minimal expert profile data (picture and name)
 * Used for displaying profile info in navigation/header components
 */
export function useExpertMiniProfile() {
  const authStore = useAuthStore();
  const userId = computed(() => authStore.user?.id ?? null);
  const isExpertSession = computed(() => Boolean(authStore.token && authStore.user?.role_id === 3));

  const { data, isLoading, error } = useQuery({
    queryKey: computed(() => ["expert", "me", userId.value]),
    queryFn: async () => {
      // TODO: Replace this garbage with useApi() from @shopexperts/shared
      const response = await ApiService.get("/v2/expert/me");
      return response.data as ExpertMiniProfileResponse;
    },
    enabled: isExpertSession,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const profilePicture = computed(() => data.value?.data?.profile_picture || "");
  const name = computed(() => data.value?.data?.name || "");

  return {
    profilePicture,
    name,
    isLoading,
    error,
  };
}

export default useExpertMiniProfile;
